/**
 * Admin/ColumnMappingEditor.tsx
 *
 * Lets the admin view and correct the column-ID mappings that the installer
 * wrote into monday Storage under the key "column_mappings".
 *
 * Each mapping links a logical name (e.g. "priority") to the actual
 * monday.com column ID on a specific board so that the app can read/write
 * the right column regardless of how the board was set up.
 *
 * Data shape (from @core/types):
 *   ColumnMapping[]  →  array of { boardId, boardName, columns: ColumnMappingEntry[] }
 *   ColumnMappingEntry → { logicalName, columnId, columnType, columnTitle }
 */

import { useState, useEffect, useCallback } from 'react';
import { storageGet, storageSet } from '@core/api/client';
import type { ColumnMapping, ColumnMappingEntry } from '@core/types';

// ─── Local types ──────────────────────────────────────────────────────────────

type EditorState = 'loading' | 'idle' | 'dirty' | 'saving' | 'saved' | 'error';

// Deep-clone helper so edits don't mutate the fetched data directly
function cloneMappings(src: ColumnMapping[]): ColumnMapping[] {
  return src.map((m) => ({
    ...m,
    columns: m.columns.map((c) => ({ ...c })),
  }));
}

// ─── Component ────────────────────────────────────────────────────────────────

export function ColumnMappingEditor() {
  const [mappings, setMappings] = useState<ColumnMapping[]>([]);
  const [editorState, setEditorState] = useState<EditorState>('loading');
  const [statusMessage, setStatusMessage] = useState('');
  const [expandedBoard, setExpandedBoard] = useState<string | null>(null);

  // ── Load ────────────────────────────────────────────────────────────────────

  const loadMappings = useCallback(async () => {
    setEditorState('loading');
    setStatusMessage('');
    try {
      const stored = await storageGet<ColumnMapping[]>('column_mappings');
      if (stored && stored.length > 0) {
        setMappings(cloneMappings(stored));
        setExpandedBoard(stored[0].boardId); // auto-expand first board
      } else {
        setMappings([]);
      }
      setEditorState('idle');
    } catch (err) {
      setStatusMessage(`Failed to load mappings: ${(err as Error).message}`);
      setEditorState('error');
    }
  }, []);

  useEffect(() => {
    void loadMappings();
  }, [loadMappings]);

  // ── Save ────────────────────────────────────────────────────────────────────

  async function saveMappings() {
    setEditorState('saving');
    setStatusMessage('Saving column mappings…');
    try {
      await storageSet('column_mappings', mappings);
      setStatusMessage('Mappings saved successfully.');
      setEditorState('saved');
    } catch (err) {
      setStatusMessage(`Save failed: ${(err as Error).message}`);
      setEditorState('error');
    }
  }

  // ── Edit helpers ────────────────────────────────────────────────────────────

  function updateEntry(
    boardId: string,
    logicalName: string,
    field: keyof ColumnMappingEntry,
    value: string,
  ) {
    setMappings((prev) =>
      prev.map((board) =>
        board.boardId !== boardId
          ? board
          : {
              ...board,
              columns: board.columns.map((col) =>
                col.logicalName !== logicalName ? col : { ...col, [field]: value },
              ),
            },
      ),
    );
    setEditorState('dirty');
  }

  // ── UI ──────────────────────────────────────────────────────────────────────

  const isDisabled = editorState === 'saving' || editorState === 'loading';

  return (
    <div className="cos-admin-card">
      <div className="cos-admin-card__header">
        <h3 className="cos-admin-heading">Column Mapping Editor</h3>
        <div className="cos-admin-card__actions">
          <button
            type="button"
            className="cos-btn cos-btn--secondary cos-btn--sm"
            onClick={() => void loadMappings()}
            disabled={isDisabled}
          >
            ↺ Refresh
          </button>
          {editorState === 'dirty' && (
            <button
              type="button"
              className="cos-btn cos-btn--primary cos-btn--sm"
              onClick={() => void saveMappings()}
            >
              Save Changes
            </button>
          )}
        </div>
      </div>

      <p className="cos-admin-muted">
        These mappings were written by the installer. Edit a Column ID if a monday.com board
        column does not match what was detected automatically.
      </p>

      {/* Status banners */}
      {editorState === 'loading' && (
        <div className="cos-admin-banner cos-admin-banner--info">
          <span className="cos-spinner" aria-hidden="true" /> Loading column mappings…
        </div>
      )}
      {editorState === 'saved' && (
        <div className="cos-admin-banner cos-admin-banner--success">✓ {statusMessage}</div>
      )}
      {editorState === 'error' && (
        <div className="cos-admin-banner cos-admin-banner--error">✗ {statusMessage}</div>
      )}
      {editorState === 'saving' && (
        <div className="cos-admin-banner cos-admin-banner--info">
          <span className="cos-spinner" aria-hidden="true" /> {statusMessage}
        </div>
      )}

      {/* Empty state */}
      {editorState !== 'loading' && mappings.length === 0 && (
        <div className="cos-admin-empty">
          <p>No column mappings found in Storage.</p>
          <p className="cos-admin-muted">
            Run the installer to populate mappings, then return here to review them.
          </p>
        </div>
      )}

      {/* Board accordion */}
      {mappings.map((board) => (
        <section key={board.boardId} className="cos-admin-section">
          <button
            type="button"
            className="cos-admin-accordion-trigger"
            onClick={() =>
              setExpandedBoard(expandedBoard === board.boardId ? null : board.boardId)
            }
            aria-expanded={expandedBoard === board.boardId}
          >
            <span className="cos-admin-accordion-icon">
              {expandedBoard === board.boardId ? '▾' : '▸'}
            </span>
            <span className="cos-admin-subheading">{board.boardName}</span>
            <span className="cos-admin-muted cos-admin-badge">
              ID: {board.boardId} · {board.columns.length} column
              {board.columns.length !== 1 ? 's' : ''}
            </span>
          </button>

          {expandedBoard === board.boardId && (
            <div className="cos-admin-table-wrap">
              <table className="cos-admin-table">
                <thead>
                  <tr>
                    <th>Logical Name</th>
                    <th>Column Title</th>
                    <th>Type</th>
                    <th>Column ID</th>
                  </tr>
                </thead>
                <tbody>
                  {board.columns.map((col) => (
                    <tr key={col.logicalName}>
                      <td>
                        <code className="cos-code">{col.logicalName}</code>
                      </td>
                      <td className="cos-admin-muted">{col.columnTitle || '—'}</td>
                      <td>
                        <span className="cos-admin-type-badge">{col.columnType}</span>
                      </td>
                      <td>
                        <input
                          type="text"
                          className="cos-admin-input cos-admin-input--mono"
                          value={col.columnId}
                          disabled={isDisabled}
                          onChange={(e) =>
                            updateEntry(board.boardId, col.logicalName, 'columnId', e.target.value)
                          }
                          aria-label={`Column ID for ${col.logicalName}`}
                          spellCheck={false}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      ))}

      {/* Sticky save bar — only shows when there are unsaved changes */}
      {editorState === 'dirty' && (
        <div className="cos-admin-sticky-bar">
          <span className="cos-admin-muted">You have unsaved changes.</span>
          <button
            type="button"
            className="cos-btn cos-btn--primary cos-btn--sm"
            onClick={() => void saveMappings()}
          >
            Save Changes
          </button>
          <button
            type="button"
            className="cos-btn cos-btn--secondary cos-btn--sm"
            onClick={() => void loadMappings()}
          >
            Discard
          </button>
        </div>
      )}
    </div>
  );
}
